// src/components/ManualSOProcessor.js - Modern Flat Design
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';

const ManualSOProcessor = () => {
  const [soNumber, setSoNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!soNumber.trim()) {
      toast.error('กรุณากรอกหมายเลข SO');
      return;
    }

    try {
      setProcessing(true);
      setResult(null);

      const response = await axios.post(`${API_URL}/process-manual-so`, {
        soNumber: soNumber.trim()
      });

      setResult(response.data);

      if (response.data.success) {
        toast.success('ประมวลผล Sales Order สำเร็จ!');
      } else {
        toast.error('การประมวลผลล้มเหลว: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error processing SO:', error);
      const errorMessage = error.response?.data?.error || error.message;
      setResult({
        success: false,
        error: errorMessage
      });
      toast.error('Error: ' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return <PageLoader message="กำลังประมวลผล Sales Order..." />;
  }

  return (
    <div className="manual-so">
      {/* Header */}
      <div className="manual-so__header">
        <div>
          <h2 className="manual-so__title">
            <i className="bi bi-file-earmark-plus-fill me-2"></i>
            Manual SO Processor
          </h2>
          <p className="manual-so__subtitle">ประมวลผล Sales Order ด้วยตนเองจากหมายเลข SO</p>
        </div>
      </div>

      {/* Search Form */}
      <div className="manual-so__search-card">
        <form onSubmit={handleSubmit} className="manual-so__form">
          <div className="manual-so__form-group">
            <label className="manual-so__label">
              <i className="bi bi-hash me-2"></i>
              หมายเลข Sales Order
            </label>
            <div className="manual-so__input-group">
              <input
                type="text"
                className="manual-so__input"
                placeholder="กรอกหมายเลข SO (เช่น SO2025001)"
                value={soNumber}
                onChange={(e) => setSoNumber(e.target.value.trim())}
                disabled={processing}
                autoFocus
              />
              <button
                type="submit"
                className="manual-so__submit-btn"
                disabled={processing || !soNumber.trim()}
              >
                <i className="bi bi-play-circle-fill me-2"></i>
                ประมวลผล
              </button>
            </div>
            <p className="manual-so__hint">
              <i className="bi bi-info-circle me-1"></i>
              ระบบจะดึงข้อมูลจาก NetSuite และบันทึกลงฐานข้อมูล
            </p>
          </div>
        </form>
      </div>

      {/* Results */}
      {result && (
        <div className={`manual-so__result manual-so__result--${result.success ? 'success' : 'error'}`}>
          <div className="manual-so__result-header">
            <div className="manual-so__result-icon">
              <i className={`bi ${result.success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
            </div>
            <div>
              <h3 className="manual-so__result-title">
                {result.success ? 'ประมวลผลสำเร็จ' : 'ประมวลผลล้มเหลว'}
              </h3>
              <p className="manual-so__result-subtitle">
                {result.success
                  ? `Sales Order ${result.so_number} ถูกประมวลผลแล้ว`
                  : result.error || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ'
                }
              </p>
            </div>
          </div>

          {result.success && (
            <>
              {/* NetSuite Details */}
              {result.netsuite_id && (
                <div className="manual-so__details">
                  <div className="manual-so__detail-item">
                    <span className="manual-so__detail-label">NetSuite ID:</span>
                    <span className="manual-so__detail-value">{result.netsuite_id}</span>
                  </div>
                  {result.netsuite_tranId && (
                    <div className="manual-so__detail-item">
                      <span className="manual-so__detail-label">Transaction ID:</span>
                      <span className="manual-so__detail-value">{result.netsuite_tranId}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="manual-so__actions">
                {result.netsuite_url && (
                  <a
                    href={result.netsuite_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="manual-so__action-btn manual-so__action-btn--primary"
                  >
                    <i className="bi bi-box-arrow-up-right me-2"></i>
                    เปิดใน NetSuite
                  </a>
                )}
                {result.edit_url && (
                  <a
                    href={result.edit_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="manual-so__action-btn manual-so__action-btn--secondary"
                  >
                    <i className="bi bi-pencil me-2"></i>
                    แก้ไขคำสั่งซื้อ
                  </a>
                )}
              </div>

              {/* Processing Logs */}
              {result.logs && result.logs.length > 0 && (
                <details className="manual-so__logs">
                  <summary className="manual-so__logs-summary">
                    <i className="bi bi-terminal me-2"></i>
                    ดู Processing Logs
                  </summary>
                  <div className="manual-so__logs-content">
                    {result.logs.map((log, index) => (
                      <div key={index} className="manual-so__log-line">
                        <span className="manual-so__log-index">{index + 1}</span>
                        <span className="manual-so__log-text">{log}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {/* Quick Tips */}
      <div className="manual-so__tips">
        <h4 className="manual-so__tips-title">
          <i className="bi bi-lightbulb me-2"></i>
          วิธีใช้งาน
        </h4>
        <ul className="manual-so__tips-list">
          <li>กรอกหมายเลข SO ที่ต้องการประมวลผล</li>
          <li>ระบบจะดึงข้อมูลจาก NetSuite อัตโนมัติ</li>
          <li>ข้อมูลจะถูกบันทึกลงฐานข้อมูล VERP</li>
          <li>สามารถเปิดดูหรือแก้ไขได้ทันทีหลังประมวลผลเสร็จ</li>
        </ul>
      </div>
    </div>
  );
};

export default ManualSOProcessor;
